import React, { useState } from "react";

import { Button, TextField, Select, MenuItem, Typography, Box, FormControl, InputLabel, RadioGroup, FormControlLabel, Radio, Checkbox, Card, CardContent } from '@mui/material'; // Assuming Material-UI is used
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {initialFormState, breakerTypes, textFieldProperties, linkMaterialOptions, linkConfigTextFieldProperties, panelConfigTextFieldProperties, installationTypeOptions, ipRatingOptions, monthOptions, panelColorOptions, panelOrientationOptions} from "./constants.js"
import { useSelector } from "react-redux";
import { calculateThermalManagement } from "../../Restclient/datacollection.js";

export default function ThermalManagement() {
  const theme = useSelector((state) => state.themedata.theme);
  const [form, setForm] = useState(initialFormState);
  const [showSpreaderThickness, setShowSpreaderThickness] = useState(false);
  const [showSolarConfig, setShowSolarConfig] = useState(false);
  const [showCFM, setShowCFM] = useState(false);
  const [showVentilation, setShowVentilation] = useState(false);
  const [showBreakerCustomFields, setShowBreakerCustomFields] = useState(false);
  const [showLinkRectangleFields, setShowLinkRectangleFields] = useState(true);
  const [results, setResults] = useState(null);

  // State to manage dropdown visibility
  const [openSections, setOpenSections] = useState({
    systemConfig: false,
    breakerConfig: false,
    linkConfig: false,
    panelConfig: false,
    installationConfig: false,
    solarConfig: false,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleChange = (e) => {
    const { id, value, type, checked, name } = e.target;
    if (type === "checkbox" && name === "coveredSurfaces") {
      setForm((prev) => ({
        ...prev,
        coveredSurfaces: checked
          ? [...prev.coveredSurfaces, value]
          : prev.coveredSurfaces.filter((v) => v !== value),
      }));
    } else if (type === "radio") {
      if (name === "spreaderUsed") {
        setShowSpreaderThickness(value === "yes");
        setForm((prev) => ({ ...prev, spreaderUsed: value }));
      } else if (name === "solarToggle") {
        setShowSolarConfig(value === "yes");
        setForm((prev) => ({ ...prev, solarToggle: value }));
      } else if (name === "ventilation") {
        setForm((prev) => ({ ...prev, ventilation: value }));
      } else if (name === "solarEnv") {
        setForm((prev) => ({ ...prev, solarEnv: id }));
      }
    } else if (name === "breakerDistribution") {
      setShowBreakerCustomFields(value === "Custom-spacing");
      setForm((prev) => ({ ...prev, [name]: value }));
    } else if (name === "linkType") {
      setShowLinkRectangleFields(value === "Rectangle");
      setForm((prev) => ({ ...prev, [name]: value }));
    } else {
      // Use name if available, otherwise fall back to id
      const fieldName = name || id;
      setForm((prev) => ({ ...prev, [fieldName]: value }));
    }
  };

  const handleIPRatingChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, ipRating: value }));
    setShowCFM(value === "Fan Cooling");
    setShowVentilation(value === "IP4X");
  };

  // Map form data to API request structure
  const mapFormDataToAPI = (formData) => {
    // Map month name to number
    const monthMap = {
      "January": 1, "February": 2, "March": 3, "April": 4,
      "May": 5, "June": 6, "July": 7, "August": 8,
      "September": 9, "October": 10, "November": 11, "December": 12
    };

    // Map panel orientation to number
    const orientationMap = {
      "South": 1, "North": 2, "East": 3, "West": 4
    };

    // Map panel color to absorption coefficient (simplified mapping)
    const colorMap = {
      "White(metal)": 0.3,
      "Cream(metal)": 0.4,
      "Yellow(metal)": 0.5,
      "Light:Grey,blue,green(metal)": 0.6,
      "Medium:Grey,blue,green(metal)": 0.7,
      "Dark:Grey,blue,green(metal)": 0.8,
      "Black(metal)": 0.9,
      "Light(Plastic)": 0.4,
      "Medium(Plastic)": 0.6,
      "Dark(Plastic)": 0.8
    };

    // Map breaker distribution
    const distributionMap = {
      "Equally spaced": 0,
      "Custom-spacing": 1
    };

    // Breaker types are already mapped to numeric values (1-19)

    // Helper function to safely parse numbers
    const safeParseFloat = (value, defaultValue = 0) => {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    };

    // Helper function to safely parse strings
    const safeParseString = (value, defaultValue = "") => {
      return value ? String(value) : defaultValue;
    };

    // Helper function to convert yes/no to boolean
    const toBoolean = (value) => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        return value.toLowerCase() === 'yes' || value.toLowerCase() === 'true';
      }
      return false;
    };

    return {
      // Required Fields
      breaker: safeParseString(formData.breaker, "8"),
      current_y: safeParseFloat(formData.current_y, 2500),
      panelHeight: safeParseFloat(formData.panelHeight, 2.1),
      panelWidth: safeParseFloat(formData.panelWidth, 0.8),
      panelDepth: safeParseFloat(formData.panelDepth, 0.8),
      ambient: safeParseFloat(formData.ambient, 32.2),
      
      // Additional current fields
      current_r: safeParseFloat(formData.current_r, safeParseFloat(formData.current_y, 2500)),
      current_b: safeParseFloat(formData.current_b, safeParseFloat(formData.current_y, 2500)),
      
      // Busbar and link configuration
      busbar: safeParseString(formData.busbar, "Copper"),
      linkResistivity: safeParseFloat(formData.hotResistivity, 2.26e-8),
      linkLength: safeParseFloat(formData.linkLength, 2.9),
      CROSSSection: safeParseFloat(formData.crossSection, 2000e-6),
      current_cl: safeParseFloat(formData.current_cl, safeParseFloat(formData.current_y, 2500)),
      linksPerPole: safeParseFloat(formData.linksPerPole, 4),
      linkWidth: safeParseFloat(formData.linkWidth, 0.1),
      linkThickness: safeParseFloat(formData.linkThickness, 0.005),
      linkType: safeParseString(formData.linkType, "Rectangle"),
      spreaderYes: toBoolean(formData.spreaderUsed),
      upperTempLimit: safeParseFloat(formData.upperTempLimit, 100),
      
      // Panel configuration
      links_total_length: safeParseFloat(formData.totalLength, safeParseFloat(formData.linkLength, 2.9)),
      numPartitions: safeParseFloat(formData.numPartitions, 0),
      numBreakers: safeParseFloat(formData.numBreakers, 1),
      breakerDistribution: distributionMap[formData.breakerDistribution] || 0,
      topBreakerHeight: safeParseFloat(formData.topBreakerHeight, 0),
      breakerGap: safeParseFloat(formData.breakerGap, 0),
      additionalPower: safeParseFloat(formData.additionalPower, 0),
      breakerPositionTop: safeParseFloat(formData.breakerPositionTop, 1),
      
      // Solar and environmental (boolean values)
      solarYes: toBoolean(formData.solarToggle),
      top: formData.coveredSurfaces.includes('Top'),
      bottom: formData.coveredSurfaces.includes('Bottom'),
      left: formData.coveredSurfaces.includes('Left'),
      right: formData.coveredSurfaces.includes('Right'),
      front: formData.coveredSurfaces.includes('Front'),
      rear: formData.coveredSurfaces.includes('Rear'),
      ipRating: safeParseString(formData.ipRating, "IP5X"),
      openAirTR_top_y0: safeParseFloat(formData.openAirTR_top_y, 56.1),
      
      // Optional fields (not in example but available)
      month: monthMap[formData.month] || 1,
      latitude: safeParseFloat(formData.latitude, 0),
      panelColor: colorMap[formData.panelColor] || 0.5,
      panelOrientation: orientationMap[formData.panelOrientation] || 1,
      OutdoorWS: formData.solarEnv === "Outdoor with Shed",
      OutdoorS: formData.solarEnv === "Outdoor without Shed",
      Indoor: formData.solarToggle === "no",
      insulationThickness: safeParseFloat(formData.insulationThickness, 0.0005),
      spreaderThickness: safeParseFloat(formData.spreaderThickness, 0),
      ventArea: safeParseFloat(formData.ventArea, 0),
      cfm: safeParseFloat(formData.cfm, 0),
      spreaderUsed: toBoolean(formData.spreaderUsed),
      altitude: safeParseFloat(formData.altitude, 0)
    };
  };

  const calculateThermalManagementAPI = async () => {
    try {
      // Validate required fields
      const requiredFields = ['breaker', 'current_y', 'panelHeight', 'panelWidth', 'panelDepth', 'ambient'];
      const missingFields = requiredFields.filter(field => !form[field] || form[field] === '');
      
      if (missingFields.length > 0) {
        alert(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        return;
      }

      const apiData = mapFormDataToAPI(form);
      console.log('Sending API request:', apiData);
      const results = await calculateThermalManagement(apiData);
      console.log('API response:', results);
      
      // Map API response to display format
    setResults({
        // Thermal Management Results
        TRF_RLL: results.TRF_RLL || "N/A",
        T1_ph_RLL: results.T1_ph_RLL || "N/A",
        TR_rated_RLL: results.TR_rated_RLL || "N/A",
        TR_abs_RLL: results.TR_abs_RLL || "N/A",
        I1_RLL: results.I1_RLL || "N/A",
        
        // Solar Calculation Results
        so_hg: results.so_hg || "N/A",
        so_idn: results.so_idn || "N/A",
        so_hid: results.so_hid || "N/A",
        so_vr: results.so_vr || "N/A",
        so_beta: results.so_beta || "N/A",
        so_direct_s: results.so_direct_s || "N/A",
        so_direct_n: results.so_direct_n || "N/A",
        so_direct_e: results.so_direct_e || "N/A",
        so_direct_w: results.so_direct_w || "N/A",
        so_direct_t: results.so_direct_t || "N/A"
      });
    } catch (error) {
      console.error('Calculation error:', error);
      if (error.response) {
        // Server responded with error status
        alert(`Error: ${error.response.data?.error || 'Server error occurred'}`);
      } else if (error.request) {
        // Request was made but no response received
        alert('Network error: Unable to connect to the calculation service');
      } else {
        // Something else happened
        alert('Error calculating thermal management. Please check your inputs and try again.');
      }
    }
  };

  const downloadResults = () => {
    // TODO: Implement CSV download logic
    alert("Download not implemented yet.");
  };

  const updateLinkFields = () => {
    // TODO: Implement dynamic link fields logic
  };

  return (
    <Box sx={{ backgroundColor: theme === 'dark' ? '#000' : '#f0f0f0', padding: '20px' }}>
      
      {/* Required Fields Note */}
      <Box sx={{ marginBottom: '20px', padding: '10px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', borderRadius: '4px', border: '1px solid #007bff' }}>
        <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000' }}>
          <strong>Required Fields:</strong> Breaker Type, Y Phase Current, Panel Dimensions (Height, Width, Depth), and Ambient Temperature must be filled before calculation.
        </Typography>
      </Box>
      
      {/* System Configuration Section */}
      <Card sx={{ marginBottom: '20px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#007bff', textAlign: 'left', marginBottom: '10px', cursor: 'pointer' }} onClick={() => toggleSection('systemConfig')}>
            System Configuration {openSections.systemConfig ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Typography>
          {openSections.systemConfig && (
            <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
              <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <InputLabel id="breaker-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff' ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Breaker Type *</InputLabel>
                <Select
                  labelId="breaker-label"
                  id="breaker"
                  value={form.breaker}
                  onChange={handleChange}
                  name="breaker"
                  sx={{ 
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  }}
                >
                  <MenuItem value="" >-- Select --</MenuItem>
                  {breakerTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {textFieldProperties.map((field) => (
                <TextField
                  key={field.id}
                  id={field.id}
                  name={field.id}
                  label={field.label}
                  type={field.type}
                  value={form[field.id]}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  sx={{
                    backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiInputBase-input': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiInputLabel-root': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Breaker Configuration Section */}
      <Card sx={{ marginBottom: '20px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
        <CardContent sx={{ color: theme === 'dark' ? '#fff' : '#000' }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#007bff', textAlign: 'left', marginBottom: '10px', cursor: 'pointer' }} onClick={() => toggleSection('breakerConfig')}>
            Breaker Configuration {openSections.breakerConfig ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Typography>
          {openSections.breakerConfig && (
            <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}>
              <TextField
                id="numBreakers"
                name="numBreakers"
                label="Number of Breakers"
                type="number"
                value={form.numBreakers}
                onChange={handleChange}
                fullWidth
                margin="normal"
                sx={{
                  backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                  color: theme === 'dark' ? '#C1C1C1' : '#000',
                  '& .MuiInputBase-input': {
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                  },
                  '& .MuiInputLabel-root': {
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover fieldset': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  },
                }}
              />
              <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <InputLabel id="breakerDistribution-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff' ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Breaker Distribution</InputLabel>
                <Select
                  labelId="breakerDistribution-label"
                  id="breakerDistribution"
                  value={form.breakerDistribution}
                  onChange={handleChange}
                  name="breakerDistribution"
                  sx={{ 
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  }}
                >
                  <MenuItem value="Equally spaced">Equally spaced</MenuItem>
                  <MenuItem value="Custom-spacing">Custom-spacing</MenuItem>
                </Select>
              </FormControl>
              {showBreakerCustomFields && (
                <>
                  <TextField
                    id="topBreakerHeight"
                    name="topBreakerHeight"
                    label="Height of Top Breaker (m)"
                    type="number"
                    value={form.topBreakerHeight}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    sx={{
                      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                      '& .MuiInputBase-input': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiInputLabel-root': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      },
                    }}
                  />
                  <TextField
                    id="breakerGap"
                    name="breakerGap"
                    label="Gap Between Breakers (m)"
                    type="number"
                    value={form.breakerGap}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    sx={{
                      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                      '& .MuiInputBase-input': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiInputLabel-root': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      },
                    }}
                  />
                </>
              )}
              <FormControl component="fieldset" margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <RadioGroup
                  row
                  aria-label="spreaderUsed"
                  name="spreaderUsed"
                  value={form.spreaderUsed}
                  onChange={handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
              {showSpreaderThickness && (
                <TextField
                  id="spreaderThickness"
                  name="spreaderThickness"
                  label="Spreader Thickness (m)"
                  type="number"
                  value={form.spreaderThickness}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  sx={{
                    backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiInputBase-input': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiInputLabel-root': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                    },
                  }}
                />
              )}
              <TextField
                id="additionalPower"
                name="additionalPower"
                label="Additional Power Source (W)"
                type="number"
                value={form.additionalPower}
                onChange={handleChange}
                fullWidth
                margin="normal"
                sx={{
                  backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                  color: theme === 'dark' ? '#C1C1C1' : '#000',
                  '& .MuiInputBase-input': {
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                  },
                  '& .MuiInputLabel-root': {
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover fieldset': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  },
                }}
              />
              <TextField
                id="breakerPositionTop"
                name="breakerPositionTop"
                label="Breaker Position Top"
                type="number"
                value={form.breakerPositionTop}
                onChange={handleChange}
                fullWidth
                margin="normal"
                sx={{
                  backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                  color: theme === 'dark' ? '#C1C1C1' : '#000',
                  '& .MuiInputBase-input': {
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                  },
                  '& .MuiInputLabel-root': {
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover fieldset': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Link Configuration Section */}
      <Card sx={{ marginBottom: '20px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#007bff', textAlign: 'left', marginBottom: '10px', cursor: 'pointer' }} onClick={() => toggleSection('linkConfig')}>
            Link Configuration {openSections.linkConfig ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Typography>
          {openSections.linkConfig && (
            <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
              <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <InputLabel id="busbar-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff' ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Link Material</InputLabel>
                <Select
                  labelId="busbar-label"
                  id="busbar"
                  value={form.busbar}
                  onChange={handleChange}
                  name="busbar"
                  sx={{ 
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  }}
                >
                  {linkMaterialOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <InputLabel id="linkType-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff' ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Link Type</InputLabel>
                <Select
                  labelId="linkType-label"
                  id="linkType"
                  value={form.linkType}
                  onChange={handleChange}
                  name="linkType"
                  sx={{ 
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  }}
                >
                  <MenuItem value="Rectangle">Rectangle</MenuItem>
                  <MenuItem value="Round">Round</MenuItem>
                  <MenuItem value="Square">Square</MenuItem>
                </Select>
              </FormControl>
              {linkConfigTextFieldProperties.map((field) => {
                // Show different fields based on link type
                if (showLinkRectangleFields) {
                  // For Rectangle: show width and thickness, hide cross section and insulation
                  if (field.id === 'crossSection' || field.id === 'insulationThickness') {
                    return null;
                  }
                } else {
                  // For Round/Square: show cross section and insulation, hide width and thickness
                  if (field.id === 'linkWidth' || field.id === 'linkThickness') {
                    return null;
                  }
                }
                
                return (
                  <TextField
                    key={field.id}
                    id={field.id}
                    name={field.id}
                    label={field.label}
                    type={field.type}
                    value={form[field.id]}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    sx={{
                      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                      '& .MuiInputBase-input': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiInputLabel-root': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Panel Configuration Section */}
      <Card sx={{ marginBottom: '20px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#007bff', textAlign: 'left', marginBottom: '10px', cursor: 'pointer' }} onClick={() => toggleSection('panelConfig')}>
            Panel Dimensions {openSections.panelConfig ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Typography>
          {openSections.panelConfig && (
            <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
              {panelConfigTextFieldProperties.map((field) => (
                <TextField
                  key={field.id}
                  id={field.id}
                  name={field.id}
                  label={field.label}
                  type={field.type}
                  value={form[field.id]}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  sx={{
                    backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiInputBase-input': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiInputLabel-root': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Installation & Ventilation Section */}
      <Card sx={{ marginBottom: '20px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',color: theme === 'dark' ? '#fff' : '#000' }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#007bff', textAlign: 'left', marginBottom: '10px', cursor: 'pointer' }} onClick={() => toggleSection('installationConfig')}>
            Installation & Ventilation {openSections.installationConfig ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Typography>
          {openSections.installationConfig && (
            <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
              <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <InputLabel id="installation-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff' ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Installation Type</InputLabel>
                <Select
                  labelId="installation-label"
                  id="installation"
                  value={form.installation}
                  onChange={handleChange}
                  name="installation"
                  sx={{ 
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  }}
                >
                  {installationTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <Typography variant="subtitle1">Covered Surfaces</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.coveredSurfaces.includes('Top')}
                      onChange={handleChange}
                      value="Top"
                      sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}
                    />
                  }
                  label="Top"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.coveredSurfaces.includes('Left')}
                      onChange={handleChange}
                      value="Left"
                      sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}
                    />
                  }
                  label="Left"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.coveredSurfaces.includes('Right')}
                      onChange={handleChange}
                      value="Right"
                      sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}
                    />
                  }
                  label="Right"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.coveredSurfaces.includes('Front')}
                      onChange={handleChange}
                      value="Front"
                      sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}
                    />
                  }
                  label="Front"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.coveredSurfaces.includes('Rear')}
                      onChange={handleChange}
                      value="Rear"
                      sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}
                    />
                  }
                  label="Rear"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.coveredSurfaces.includes('Bottom')}
                      onChange={handleChange}
                      value="Bottom"
                      sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}
                    />
                  }
                  label="Bottom"
                />
              </Box>
              <FormControl component="fieldset" margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <RadioGroup
                  row
                  aria-label="ventilation"
                  name="ventilation"
                  value={form.ventilation}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Without-Ventilation" control={<Radio />} label="Without Ventilation" />
                  <FormControlLabel value="With-Ventilation" control={<Radio />} label="With Ventilation" />
                </RadioGroup>
              </FormControl>
              <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                <InputLabel id="ipRating-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff' ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>IP Rating / Cooling</InputLabel>
                <Select
                  labelId="ipRating-label"
                  id="ipRating"
                  value={form.ipRating}
                  onChange={handleIPRatingChange}
                  name="ipRating"
                  sx={{ 
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                    },
                  }}
                >
                  {ipRatingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {showVentilation && (
                <TextField
                  id="ventArea"
                  name="ventArea"
                  label="Ventilation Area (cm²)"
                  type="number"
                  value={form.ventArea}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  sx={{
                    backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiInputBase-input': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiInputLabel-root': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                    },
                  }}
                />
              )}
              {showCFM && (
                <TextField
                  id="cfm"
                  name="cfm"
                  label="CFM (Cubic Feet per Minute)"
                  type="number"
                  value={form.cfm}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  sx={{
                    backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                    color: theme === 'dark' ? '#C1C1C1' : '#000',
                    '& .MuiInputBase-input': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiInputLabel-root': {
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                      },
                    },
                  }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Solar Configuration Section */}
      <Card sx={{ marginBottom: '20px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#007bff', textAlign: 'left', marginBottom: '10px', cursor: 'pointer' }} onClick={() => toggleSection('solarConfig')}>
            Solar Radiation Configuration {openSections.solarConfig ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Typography>
          {openSections.solarConfig && (
            <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}>
              <FormControl component="fieldset" margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff', color: theme === 'dark' ? '#fff' : '#000' }}>
                <RadioGroup
                  row
                  aria-label="solarToggle"
                  name="solarToggle"
                  value={form.solarToggle}
                  onChange={handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No (Indoor)" />
                </RadioGroup>
              </FormControl>
              {showSolarConfig && (
                <Box sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                  <TextField
                    id="date"
                    name="date"
                    label="Date"
                    type="date"
                    value={form.date}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                      '& .MuiInputBase-input': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiInputLabel-root': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      },
                    }}
                  />
                  <TextField
                    id="time"
                    name="time"
                    label="Time"
                    type="time"
                    value={form.time}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                      '& .MuiInputBase-input': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiInputLabel-root': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      },
                    }}
                  />
                  <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                    <InputLabel id="solarEnv-label" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Installation Environment</InputLabel>
                    <Select
                      labelId="solarEnv-label"
                      id="solarEnv"
                      value={form.solarEnv}
                      onChange={handleChange}
                      name="solarEnv"
                      sx={{ 
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      }}
                    >
                      <MenuItem value="Outdoor without Shed">Outdoor without Shed</MenuItem>
                      <MenuItem value="Outdoor with Shed">Outdoor with Shed</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                    <InputLabel id="month-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff' ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Month</InputLabel>
                    <Select
                      labelId="month-label"
                      id="month"
                      value={form.month}
                      onChange={handleChange}
                      name="month"
                      sx={{ 
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      }}
                    >
                      {monthOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    id="altitude"
                    name="altitude"
                    label="Altitude (m)"
                    type="number"
                    value={form.altitude}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    sx={{
                      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                      '& .MuiInputBase-input': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiInputLabel-root': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      },
                    }}
                  />
                  <TextField
                    id="latitude"
                    name="latitude"
                    label="Latitude"
                    type="number"
                    value={form.latitude}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    sx={{
                      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
                      color: theme === 'dark' ? '#C1C1C1' : '#000',
                      '& .MuiInputBase-input': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiInputLabel-root': {
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      },
                    }}
                  />
                  <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                    <InputLabel id="panelColor-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff'  ,paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Panel Color</InputLabel>
                    <Select
                      labelId="panelColor-label"
                      id="panelColor"
                      value={form.panelColor}
                      onChange={handleChange}
                      name="panelColor"
                      sx={{ 
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      }}
                    >
                      {panelColorOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth margin="normal" sx={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
                    <InputLabel id="panelOrientation-label" sx={{ bgcolor: theme === 'dark' ? '#1e1e1e' : '#fff',paddingLeft: '10px',paddingRight: '10px',color: theme === 'dark' ? '#C1C1C1' : '#000', borderColor: theme === 'dark' ? '#C1C1C1' : '#000' }}>Panel Front Orientation</InputLabel>
                    <Select
                      labelId="panelOrientation-label"
                      id="panelOrientation"
                      value={form.panelOrientation}
                      onChange={handleChange}
                      name="panelOrientation"
                      sx={{ 
                        color: theme === 'dark' ? '#C1C1C1' : '#000',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme === 'dark' ? '#007bff' : '#007bff',
                        },
                      }}
                    >
                        {panelOrientationOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Button variant="contained" color="primary" onClick={calculateThermalManagementAPI} fullWidth>
        Calculate
      </Button>

      {results && (
        <Card sx={{ marginTop: '20px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff' }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#007bff', textAlign: 'center', marginBottom: '20px' }}>
              Calculation Results
            </Typography>
            
            {/* Thermal Management Results */}
            <Box sx={{ marginBottom: '20px' }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ color: '#007bff', marginBottom: '15px' }}>
                Thermal Management Results
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Left Column */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                      IPOO Terminal TR (°C):
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                      {results.TRF_RLL} °C
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                      Micro-ambient Temperature Rise:
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                      {results.T1_ph_RLL} °C
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                      In Panel Terminal TR (°C):
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                      {results.TR_rated_RLL} °C
                    </Typography>
                  </Box>
                </Box>
                
                {/* Right Column */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                      Absolute Terminal Temperature:
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                      {results.TR_abs_RLL} °C
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                      Derated Current:
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                      {results.I1_RLL} A
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            
            {/* Solar Results Section
            {results.so_beta && results.so_beta !== "N/A" && (
              <Box sx={{ marginTop: '20px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Heat Gain:
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_hg}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Direct Normal Irradiance:
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_idn}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Horizontal Irradiance:
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_hid}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Reflected Irradiance:
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_vr}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Elevation Angle:
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_beta}°
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Direct Irradiance (South):
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_direct_s}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Direct Irradiance (North):
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_direct_n}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Direct Irradiance (East):
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_direct_e}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Direct Irradiance (West):
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_direct_w}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid', borderColor: theme === 'dark' ? '#333' : '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#C1C1C1' : '#000', fontWeight: '500' }}>
                        Solar Direct Irradiance (Top):
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>
                        {results.so_direct_t}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )} */}
            
            {/* Download Button */}
            <Box sx={{ textAlign: 'center', marginTop: '20px' }}>
              <Button
                variant="outlined"
              onClick={downloadResults}
                sx={{
                  borderColor: theme === 'dark' ? '#C1C1C1' : '#000',
                  color: theme === 'dark' ? '#C1C1C1' : '#000',
                  '&:hover': {
                    borderColor: '#007bff',
                    color: '#007bff',
                    backgroundColor: theme === 'dark' ? 'rgba(0, 123, 255, 0.1)' : 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                Download Results as CSV
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}